import FroalaEditor from 'froala-editor';
import { FROALA_PENCIL_ICON_SVG } from 'libs/shared/feature-flowchart/src/lib/infrastructure/operations/cached-icons';
import { InternalLinkSearchResult } from 'shared/domain';

import { LinkElementType } from '@treetop/shared/model';

export const ADD_ANCHOR_LINK_BUTTON_PREFIX = 'add-anchor-link-button';
export const ADD_ANCHOR_LINK_CONTAINER_PREFIX = 'custom-popup-container_anchor-link';

export const addAnchorLinkFroalaPopup = (title: string) => {
  FroalaEditor.DefineIcon('ttAnchorLinkIcon', { NAME: 'anchor', SVG_KEY: 'insertEmbed' });
  FroalaEditor.RegisterCommand(ADD_ANCHOR_LINK_BUTTON_PREFIX, {
    id: ADD_ANCHOR_LINK_BUTTON_PREFIX,
    title,
    icon: 'ttAnchorLinkIcon',
    undo: false,
    focus: false,
    plugin: 'ttAnchorLinkPlugin',
    callback: function () {
      const callback = this.opts._pendingAttachAnchorLinkPopup;
      if (!callback) {
        return;
      }

      this.ttAnchorLinkPlugin.showPopup(callback);
    },
  });
};

export const registerAddAnchorLinkCommand = () => {
  FroalaEditor.RegisterCommand('addAnchorLink', {
    title: 'Add Anchor Link',
    focus: true,
    undo: true,
    refreshAfterCallback: true,
    callback: function () {
      const link = this.opts._pendingAnchorLink;
      if (!link) return;

      if (link.externalGuidelineUniqueId && link.toChapterUniqueId) {
        this.html.insert(
          `<a contenteditable="false" class="fr-deletable"
         data-element-id="${link.externalGuidelineUniqueId}#${link.uniqueId}"
         data-element-type="${LinkElementType.Guideline}"
         data-chapter-id="${link.toChapterUniqueId}">${link.title}</a>`,
        );
      } else {
        this.html.insert(
          `<a contenteditable="false" class="fr-deletable"
         data-element-id="${link.uniqueId}"
         data-element-type="${link.toElementType}"
         data-chapter-id="${link.uniqueId}">${link.title}</a>`,
        );
      }

      this.opts._pendingAnchorLink = null;
    },
  });
};

export const createFroalaAnchorLinkPlugin = () => {
  FroalaEditor.PLUGINS.AnchorLink = function (editor) {
    const addAnchorLink = (link: InternalLinkSearchResult) => {
      editor.opts._pendingAnchorLink = link;
      editor.commands.exec('addAnchorLink');
    };

    return { addAnchorLink };
  };
};

export function addAnchorLinkEditPopup(): void {
  if (FroalaEditor.PLUGINS.anchorLinkEditPopup) return;

  if (!FroalaEditor.POPUP_TEMPLATES['anchorLinkEditPopup.popup']) {
    Object.assign(FroalaEditor.POPUP_TEMPLATES, {
      'anchorLinkEditPopup.popup': '[_CUSTOM_LAYER_]',
    });
  }

  FroalaEditor.PLUGINS.anchorLinkEditPopup = function (editor: any) {
    const POPUP_ID = 'anchorLinkEditPopup.popup';
    let currentLink: HTMLAnchorElement | null = null;

    function initPopup() {
      let $popup = editor.popups.get(POPUP_ID);
      if ($popup && $popup.length) return $popup;

      const template = {
        custom_layer: `
          <div class="fr-layer fr-active anchor-link-editor-layer" id="fr-anchor-link-layer">
            <div class="fr-input-line">
              <input type="text" class="fr-link-element-id" placeholder="Guideline Unique Id">
            </div>
            <div class="fr-input-line">
              <input type="text" class="fr-link-text" placeholder="Link text">
            </div>
            <div class="fr-action-buttons">
              <button type="button" class="fr-command fr-submit" data-cmd="anchorLinkSave">Save</button>
              <button type="button" class="fr-command fr-dismiss" data-cmd="anchorLinkCancel">Cancel</button>
            </div>
          </div>
        `,
      };

      $popup = editor.popups.create(POPUP_ID, template);
      editor.popups.setContainer(POPUP_ID, editor.$box);
      return $popup;
    }

    function showPopup() {
      const link = editor.link.get();
      if (!link) return;

      currentLink = link;
      const linkType = link.getAttribute('data-element-type');
      const showIdEditor = linkType === String(LinkElementType.Guideline);
      const $popup = initPopup();
      $popup
        .find('.fr-link-element-id')
        .closest('.fr-input-line')
        .css('display', showIdEditor ? '' : 'none');
      $popup.find('.fr-link-element-id').val(link.getAttribute('data-element-id') || '');
      $popup.find('.fr-link-text').val(link.textContent || '');

      const $linkPopup = editor.popups.get('link.edit');
      const left = $linkPopup && $linkPopup.length ? $linkPopup.offset().left : 0;
      const top = $linkPopup && $linkPopup.length ? $linkPopup.offset().top + $linkPopup.outerHeight() : 0;
      const height = $linkPopup && $linkPopup.length ? $linkPopup.outerHeight() : 0;

      editor.popups.hide('link.edit');
      editor.popups.show(POPUP_ID, left, top, height);

      const $shownPopup = editor.popups.get(POPUP_ID);
      if ($shownPopup && $shownPopup.length) {
        $shownPopup.css('z-index', 10);
      }
    }

    function save() {
      const $popup = editor.popups.get(POPUP_ID);
      if (!currentLink || !$popup || !$popup.length) return;

      const elementId = String($popup.find('.fr-link-element-id').val() || '').trim();
      const text = String($popup.find('.fr-link-text').val() || '');

      if (elementId) {
        currentLink.setAttribute('data-element-id', elementId);
      } else {
        currentLink.removeAttribute('data-element-id');
      }

      currentLink.textContent = text;

      editor.undo.saveStep();
      editor.popups.hide(POPUP_ID);
      currentLink = null;
    }

    function hidePopup() {
      editor.popups.hide(POPUP_ID);
      currentLink = null;
    }

    return {
      showPopup,
      save,
      hidePopup,
    };
  };

  // @ts-ignore - The COMMANDS property is editable and extendable!
  if (!FroalaEditor.COMMANDS.anchorLinkEdit) {
    FroalaEditor.DefineIconTemplate('anchorLinkEditIconTemplate', FROALA_PENCIL_ICON_SVG);
    FroalaEditor.DefineIcon('anchorLinkEditIcon', {
      NAME: 'anchor-link-edit-icon',
      SVG_KEY: 'anchor-link-edit-svg',
      template: 'anchorLinkEditIconTemplate',
    });

    FroalaEditor.RegisterCommand('anchorLinkEdit', {
      title: 'Edit anchor link',
      icon: 'anchorLinkEditIcon',
      plugin: 'anchorLinkEditPopup',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback: function () {
        this.anchorLinkEditPopup.showPopup();
      },
    });
  }

  // @ts-ignore - The COMMANDS property is editable and extendable!
  if (!FroalaEditor.COMMANDS.anchorLinkSave) {
    FroalaEditor.RegisterCommand('anchorLinkSave', {
      title: 'Save',
      plugin: 'anchorLinkEditPopup',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback: function () {
        this.anchorLinkEditPopup.save();
      },
    });
  }

  // @ts-ignore - The COMMANDS property is editable and extendable!
  if (!FroalaEditor.COMMANDS.anchorLinkCancel) {
    FroalaEditor.RegisterCommand('anchorLinkCancel', {
      title: 'Cancel',
      plugin: 'anchorLinkEditPopup',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback: function () {
        this.anchorLinkEditPopup.hidePopup();
      },
    });
  }
}
